import { createApi } from '@reduxjs/toolkit/query/react'
import _ from 'lodash'
import { baseQuery } from 'src/utils/base-query.js'

// Define a service using a base URL and expected endpoints
export const leadsApi = createApi({
  reducerPath: 'leadsApi',
  baseQuery,
  tagTypes: ['SEARCHES'],
  endpoints: (builder) => ({
    findLeads: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'leads/find',
          body: data
        }
      },
      invalidatesTags: ['SEARCHES']
    }),
    findCompanies: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'leads/searchCompany',
          body: data
        }
      },
      invalidatesTags: ['SEARCHES']
    }),
    lookupLeads: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'leads/lookup',
          body: data
        }
      }
    }),
    lookupCompany: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'leads/company/lookup',
          body: data
        }
      }
    }),
    getSearches: builder.query({
      query: (queryParams) => {
        const params = new URLSearchParams(_.pickBy(queryParams))
        return {
          method: 'GET',
          url: '/leads/searches',
          params
        }
      },
      providesTags: ['SEARCHES']
    }),
    getSuggestions: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/leads/suggestions',
          body: data
        }
      }
    }),
    getAllSavedSearches: builder.query({
      query: (queryParams) => {
        const params = new URLSearchParams(_.pickBy(queryParams))
        return {
          method: 'GET',
          url: '/leads/searches/saved',
          params
        }
      },
      providesTags: ['SEARCHES_SAVED']
    }),
    createSavedSearch: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'leads/searches/saved',
          body: data
        }
      },
      invalidatesTags: ['SEARCHES']
    }),
    updateSearch: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `leads/searches/${id}`,
          body: data
        }
      },
      invalidatesTags: ['SEARCHES']
    }),
    deleteSearch: builder.mutation({
      query: (id) => {
        return {
          method: 'DELETE',
          url: `leads/searches/${id}`
        }
      },
      invalidatesTags: ['SEARCHES', 'SEARCHES_SAVED']
    }),
    addLeadsToCampaign: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'leads/add-to-campaign',
          body: data
        }
      }
    }),
    addLeadsToList: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'leads/add-to-list',
          body: data
        }
      }
    }),
    getLeads: builder.query({
      query: (id) => {
        return {
          method: 'GET',
          url: '/leads/' + id
        }
      },
      providesTags: ['LEADS']
    }),
    deleteLeads: builder.mutation({
      query: (data) => {
        return {
          method: 'DELETE',
          url: '/leads',
          body: data
        }
      }
    }),
    moveToCampaign: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'POST',
          url: `/leads/move-to-campaign/${id}`,
          body: data
        }
      }
    }),
    updateLead: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `/leads/${id}`,
          body: data
        }
      },
      invalidatesTags: ['LEADS']
    }),
    saveDownloadCsv: builder.mutation({
      query: ({ data }) => {
        return {
          method: 'POST',
          url: '/leads/save/csv',
          body: data
        }
      }
    }),
    getLeadsUsage: builder.query({
      query: () => {
        return {
          method: 'GET',
          url: '/leads/lead/usage'
        }
      },
      providesTags: ['LEADS_USAGE']
    }),
    getSavedFiles: builder.query({
      query: () => {
        return {
          method: 'GET',
          url: '/leads/saved/files'
        }
      },
      providesTags: ['LEADS_SAVED']
    }),
    getDownloadFile: builder.mutation({
      query: (id) => {
        return {
          method: 'GET',
          url: `leads/download/file/${id}`
        }
      }
    }),
    getSavedCompanyFiles: builder.query({
      query: () => {
        return {
          method: 'GET',
          url: '/leads/company/saved/files'
        }
      },
      providesTags: ['COMPANY_SAVED']
    }),
    getDownloadCompanyFile: builder.mutation({
      query: (id) => {
        return {
          method: 'GET',
          url: `leads/company/download/file/${id}`
        }
      }
    }),
    removeLeads: builder.mutation({
      query: (data) => {
        return {
          method: 'DELETE',
          url: '/leads/email',
          body: data
        }
      }
    }),
    changeDownloadCsvResults: builder.mutation({
      query: (id) => {
        return {
          method: 'POST',
          url: `/leads/to-open/${id}`
          // body: data,
        }
      }
    }),
    leadsConfirmation: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/leads/confirmation',
          body: data
        }
      }
    }),
    getSaveFiles: builder.mutation({
      query: (queryParams) => {
        const params = new URLSearchParams(_.pickBy(queryParams))
        return {
          method: 'GET',
          url: '/leads/saved/files',
          params
        }
      },
      providesTags: ['LEADS_SAVED']
    }),
    getSaveCompanyFiles: builder.mutation({
      query: (queryParams) => {
        const params = new URLSearchParams(_.pickBy(queryParams))
        return {
          method: 'GET',
          url: '/leads/company/saved/files',
          params
        }
      },
      providesTags: ['LEADS_SAVED']
    }),
    getleadsCampaigns: builder.mutation({
      query: (leadsIds) => {
        return {
          method: 'POST',
          url: '/leads/get/lead-campaigns-users',
          body: leadsIds
        }
      }
    }),
    getUserCampaigns: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/leads/user-campaigns',
          body: data
        }
      }
    }),
    changeLabel: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/leads/change-label',
          body: data
        }
      }
    }),
    removeDeal: builder.mutation({
      query: (data) => {
        return {
          method: 'DELETE',
          url: '/leads/remove-deal',
          body: data
        }
      }
    }),
    getCompanyIntent: builder.mutation({
      query: ({ domain, topicsArray }) => {
        if (!Array.isArray(topicsArray)) {
          console.error('Topics must be an array.')
          return
        }
        const params = new URLSearchParams()
        params.append('domain', domain)
        topicsArray.forEach(topic => {
          params.append('topics[]', topic)
        })
        return {
          url: 'https://api.b2brocket.ai/api/v1/main/intent-categories/companyIntent',
          params
        }
      }
    }),
    getLeadCampaignsByLabel: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/leads/get/lead-campaigns/labels',
          body: data
        }
      }
    }),
    addDealValue: builder.mutation({
      query: (data) => {
        return {
          method: 'PUT',
          url: `leads/add-deal/value/${data.leadId}`,
          body: data
        }
      }
    }),
        getLeadCampaignsByPipeline: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/leads/get/lead-campaigns/by-pipeline',
          body: data
        }
      }
    }),
    getLeadsCampaignByWorkspaceAndStage: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: '/leads/get/leads-campaign/by/workspace/stage',
          body: data
        }
      }
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFindLeadsMutation,
  useFindCompaniesMutation,
  useLookupLeadsMutation,
  useLookupCompanyMutation,
  useGetSearchesQuery,
  useLazyGetAllSavedSearchesQuery,
  useCreateSavedSearchMutation,
  useUpdateSearchMutation,
  useDeleteSearchMutation,
  useAddLeadsToCampaignMutation,
  useAddLeadsToListMutation,
  useGetLeadsQuery,
  useDeleteLeadsMutation,
  useMoveToCampaignMutation,
  useUpdateLeadMutation,
  useGetLeadsUsageQuery,
  useSaveDownloadCsvMutation,
  useGetSavedFilesQuery,
  useGetSavedCompanyFilesQuery,
  useGetDownloadFileMutation,
  useGetDownloadCompanyFileMutation,
  useRemoveLeadsMutation,
  useGetSuggestionsMutation,
  useChangeDownloadCsvResultsMutation,
  useLeadsConfirmationMutation,
  useGetSaveFilesMutation,
  useGetSaveCompanyFilesMutation,
  useGetleadsCampaignsMutation,
  useGetUserCampaignsMutation,
  useChangeLabelMutation,
  useRemoveDealMutation,
  useGetCompanyIntentMutation,
  useGetLeadCampaignsByLabelMutation,
  useAddDealValueMutation,
  useGetLeadCampaignsByPipelineMutation,
  useGetLeadsCampaignByWorkspaceAndStageMutation
} = leadsApi
